import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { trackEvent, EVENTS } from "../lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This page is hit by Google's redirect — we forward the `code` to the backend
// which exchanges it for tokens and sets our auth cookie.
export default function GoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  // StrictMode mounts the component twice in dev — a ref guard ensures we
  // don't burn the single-use authorization code on the first mount.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const code = params.get("code");
    const errParam = params.get("error");

    if (errParam) {
      setError(errParam === "access_denied" ? "You cancelled Google sign-in." : `Google: ${errParam}`);
      return;
    }
    if (!code) {
      setError("Missing authorization code.");
      return;
    }

    (async () => {
      // Three intents share this callback route:
      //  - "link"   → attach Google to a logged-in phone-OTP user
      //  - "delete" → forward the code to /delete-account so it can hit the
      //               public-verify-google-for-delete endpoint. We DO NOT
      //               call /auth/google here (that would log the user in
      //               instead of letting them see + confirm the deletion).
      //  - default  → fresh login or signup
      // The intent is stamped in localStorage by the trigger button. We
      // always clear it once we've read it (regardless of success or
      // failure) so a failed link doesn't accidentally route the user's
      // NEXT plain /login attempt through the link endpoint.
      let intent = "login";
      try {
        const stored = localStorage.getItem("syllox_oauth_intent");
        if (stored === "link") intent = "link";
        else if (stored === "delete") intent = "delete";
        if (intent !== "delete") {
          // For the delete flow the /delete-account page will clear the flag
          // after it consumes the code — we keep it here so that page knows
          // to look for the code in the URL.
          localStorage.removeItem("syllox_oauth_intent");
        }
      } catch { /* ignore */ }

      if (intent === "delete") {
        // Forward the raw code to the delete page. Its useEffect will POST
        // it to /account/public-verify-google-for-delete.
        navigate(`/delete-account?code=${encodeURIComponent(code)}`, { replace: true });
        return;
      }

      try {
        const endpoint = intent === "link" ? "/auth/google/link" : "/auth/google";
        const { data } = await api.post(endpoint, {
          code,
          redirect_uri: window.location.origin + "/auth/google/callback",
        });
        // Backend returns the JWT in the body alongside the user fields.
        // Persist it so the Bearer-token interceptor in api.js can attach it
        // on every subsequent call — bypassing third-party-cookie restrictions
        // on iPad Safari / Android Chrome / Brave.
        if (data?.token) setToken(data.token);
        setUser(data);
        if (intent === "link") {
          toast.success("Google account linked!");
          navigate("/settings", { replace: true });
          return;
        }
        if (data?.is_new) {
          trackEvent(EVENTS.SIGNUP_COMPLETED, { method: "google" });
        }
        toast.success(`Welcome${data?.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        // /auth/google/link returns 409 with a structured payload when the
        // Google email is already attached to ANOTHER Syllox account. Surface
        // that to the user cleanly instead of a generic "Sign-in failed".
        const detail = e?.response?.data?.detail;
        let msg;
        if (detail && typeof detail === "object" && detail.error === "google_email_in_use") {
          msg = detail.message || "This Google account is already linked to another Syllox account.";
        } else {
          msg = formatApiError(detail) || e?.message || "Sign-in failed";
        }
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-6 dark:bg-zinc-950" data-testid="google-callback-root">
      <div className="w-full max-w-sm rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto h-12 w-12">
          <Logo className="h-12 w-12" />
        </div>
        {error ? (
          <>
            <h2 className="mt-4 font-heading text-xl font-semibold">Sign-in problem</h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400" data-testid="google-callback-error">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
              data-testid="google-callback-retry"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <h2 className="mt-4 font-heading text-xl font-semibold">Signing you in…</h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Finishing up with Google.</p>
            <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
              <div className="h-full w-1/2 animate-pulse bg-emerald-500"/>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
